/** Shapes returned by the backend. Kept flat and close to the wire — the API already returns raw
 *  epoch millis and canonical type names, so there is nothing to normalise on arrival. */

export const LOG_TYPES = [
  "PAYMENT",
  "TPA",
  "SERVER_MOVEMENT",
  "ORDER",
  "AUCTION",
  "WAGERS",
  "DEATH",
  "KILL",
  "COMMAND",
  "CHAT",
  "CHAT_FILTER",
  "PUNISHMENT",
  "HOME",
  "TEAM",
  "ADMIN",
  "DUPE",
  "SPAWNER_STASH",
  "WAGER_DEBUG",
] as const;

export type LogType = (typeof LOG_TYPES)[number];

export interface LogRow {
  id: number;
  logged_at: number;
  server: string;
  type: string;
  player: string | null;
  message: string;
}

export interface LogPage {
  rows: LogRow[];
  nextCursor: string | null;
  queryMs: number;
}

export interface LogMeta {
  types: Array<{ type: LogType; aliases: string[] }>;
  servers: string[];
}

/**
 * A SPAWNER_STASH row with its `details` already parsed — see src/lib/spawner-stash.ts. `parsed` is
 * false when the payload was missing or malformed, in which case only `message` is meaningful.
 */
export interface SpawnerStash {
  id: number;
  logged_at: number;
  server: string;
  player: string | null;
  message: string;
  parsed: boolean;
  raw: string | null;
  world: string | null;
  x: number | null;
  y: number | null;
  z: number | null;
  coords: string | null;
  tp: string | null;
  block: string | null;
  spawners: number | null;
  spawners_in_shulkers: number | null;
  loose_spawners: number | null;
  shulkers_with_spawners: number | null;
  spawner_types: Array<{ mob: string; count: number | null }>;
  spawner_types_raw: string | null;
  /** Null = placed before tracking existed. Normal, not an error. */
  placed_by: string | null;
  placed_by_uuid: string | null;
  placed_at: string | null;
  placed_at_ms: number | null;
  /** "opened" or "chunk_load". */
  found_by: string | null;
  opened_by: string | null;
  opened_by_uuid: string | null;
}

/** One WAGER_DEBUG row, parsed — see src/lib/wager-debug.ts. Admin only. */
export interface WagerDebugRow {
  id: number;
  logged_at: number;
  server: string;
  player: string | null;
  player_uuid: string | null;
  message: string;
  stamp: string | null;
  wagerId: string | null;
  event: string | null;
  /** The event's own keys; every value a string or null. */
  fields: Record<string, string | null>;
  parsed: boolean;
  raw: string | null;
}

/** One wager fight, summarised from all its rows. */
export interface WagerFight {
  stamp: string;
  wagerId: string | null;
  servers: string[];
  fighters: string[];
  first_at: number;
  last_at: number;
  steps: number;
  outcome: { event: string; text: string; logged_at: number } | null;
  invincibleSuspected: boolean;
  damageWarnings: boolean;
}

export type Tier = "viewer" | "staff" | "admin";

/** Mirrors TIER_PERMISSIONS in src/auth/session.ts. The server enforces; this only decides what to
 *  show, so a mismatch hides or shows a tab but never grants anything. */
export type Permission = "logs" | "alts" | "refunds" | "voice" | "portal" | "discord" | "admin";

const TIER_PERMISSIONS: Record<Tier, readonly Permission[]> = {
  viewer: ["logs"],
  staff: ["voice", "portal", "discord"],
  admin: ["logs", "alts", "refunds", "voice", "portal", "discord", "admin"],
};

export interface DiscordEvent {
  id: number;
  occurred_at: number;
  category: string;
  action: string;
  actor_id: string | null;
  actor_name: string | null;
  target_id: string | null;
  target_name: string | null;
  channel_id: string | null;
  channel_name: string | null;
  reason: string | null;
  content: string | null;
  /** JSON string. */
  details: string | null;
}

export interface DiscordSummary {
  since: number;
  counts: Array<{ category: string; action: string; n: number }>;
}

export function can(tier: Tier, permission: Permission): boolean {
  return TIER_PERMISSIONS[tier]?.includes(permission) ?? false;
}

export interface LoaEntry {
  id: number;
  userId: string;
  username: string;
  /** YYYY-MM-DD, inclusive. */
  startsOn: string;
  endsOn: string;
  reason: string;
  createdAt: number;
}

export interface PortalPunishment extends Punishment {
  evidenceCount: number;
}

export interface Evidence {
  id: number;
  kind: string;
  punishmentId: string;
  player: string;
  fileName: string;
  contentType: string;
  bytes: number;
  note: string | null;
  uploadedById: string;
  uploadedBy: string;
  uploadedAt: number;
}

export interface SessionUser {
  id: string;
  username: string;
  displayName: string;
  tier: Tier;
}

export interface Me {
  authenticated: boolean;
  user?: SessionUser;
  /** True after signing in with an admin-issued password; nothing else works until it is changed. */
  mustChangePassword?: boolean;
  /** Whether the server saw the desktop app's User-Agent token on this request. */
  client?: { isApp: boolean; version: string | null; platform: string | null };
  csrfToken?: string;
}

export interface StaffAccount {
  id: string;
  username: string;
  tier: Tier;
  /** "env" accounts are defined in .env and cannot be managed from the dashboard. */
  source: "env" | "db";
  mustChangePassword: boolean;
  createdAt: number | null;
  createdBy: string | null;
  /** Minecraft account this staff account acts as for player-profile actions. Admin-set. */
  minecraft?: { uuid: string; name: string } | null;
}

/** Returned once, when an account is created or its password reset. */
export interface IssuedPassword {
  user: { id: string; username: string; tier: Tier };
  password: string;
}

/** A Me that has passed the auth gate, so `user` and `csrfToken` are known present rather than
 *  optional — which keeps every consumer below the gate from re-checking them. */
export interface AuthenticatedMe extends Me {
  authenticated: true;
  user: SessionUser;
  csrfToken: string;
}

export interface Punishment {
  kind: "ban" | "mute" | "kick";
  id: string | null;
  reason: string;
  issuer: string;
  issued_at: number;
  expires_at: number | null;
  active: boolean;
  ip_ban: boolean;
}

export interface PlayerSummary {
  player: string | null;
  uuid: string | null;
  banCount: number;
  muteCount: number;
  kickCount: number;
  /** False when the plugin predates kick_log — the UI must say so rather than show a confident 1. */
  kickHistoryAvailable: boolean;
  currentBan: {
    id: string;
    reason: string;
    issuer: string;
    issued_at: number;
    expires_at: number | null;
    ip_ban: boolean;
  } | null;
  currentMute: {
    id: string | null;
    reason: string;
    issuer: string;
    issued_at: number;
    expires_at: number | null;
  } | null;
  firstSeen: number | null;
  lastSeen: number | null;
  altCount: number;
  altsVisible: boolean;
}

export interface AltAccount {
  player: string;
  uuid: string | null;
  firstSeen: number | null;
  lastSeen: number | null;
}

export interface AltsResponse {
  accounts: AltAccount[];
  source: "ip_history" | "ip_log";
  /** True when only ip_log was available, which answers a much narrower question. */
  limited: boolean;
}

export interface VoiceEvent {
  id: number;
  occurred_at: number;
  server: string;
  uuid: string;
  player: string;
  transcript: string | null;
  matched_rule: string | null;
  punished: boolean;
  has_clip: boolean;
  clip_bytes: number | null;
  duration_ms: number | null;
}

export interface AuditEntry {
  id: number;
  occurred_at: number;
  user_id: string;
  username: string | null;
  action: string;
  target: string | null;
  detail: string | null;
}

export interface PunishmentEvent {
  kind: "ban" | "mute" | "kick" | "unban" | "unmute";
  id?: string;
  uuid?: string;
  player?: string;
  reason?: string;
  issuer?: string;
  issued_at?: number;
  expires_at?: number;
  ip_ban?: boolean;
  active?: boolean;
}

export type ConnectionState = "live" | "reconnecting" | "degraded-polling" | "offline";

export interface Overview {
  activity: {
    lastHour: number;
    last24h: number;
    /** True because the big counters are derived from row-id arithmetic rather than COUNT(*) —
     *  see routes/overview.ts. The UI renders them with a "~". */
    approximate: boolean;
    totalRows: number;
  };
  typeBreakdown: Array<{ type: string; count: number }>;
  topPlayers: Array<{ player: string; count: number }>;
  punishments: {
    activeBans: number;
    activeMutes: number;
    bansLast24h: number;
    mutesLast24h: number;
  };
  recentPunishments: Array<{
    kind: "ban" | "mute";
    player: string;
    reason: string;
    issuer: string;
    issued_at: number;
    active: boolean;
  }>;
  /** Null for the viewer tier, which has no access to voice data at all. */
  voice: {
    last24h: number;
    punishedLast24h: number;
    recent: Array<{
      id: number;
      player: string;
      matched_rule: string | null;
      punished: boolean;
      occurred_at: number;
      has_clip: boolean;
    }>;
  } | null;
  system: {
    live: ConnectionState;
    highestLogId: number;
    streams: { total: number; users: number };
    uptimeSeconds: number;
  };
}

/**
 * One anti-dupe incident: the void, plus the ban it triggered if there was one.
 *
 * Assembled server-side by grouping `debug_logs` rows on their shared `stamp` — see routes/dupe.ts
 * for why that grouping does not happen here.
 */
export type RefundStatus = "pending" | "claimed" | "delivered" | "failed";

export interface RefundRecord {
  id: number;
  created_at: number;
  player_uuid: string;
  player_name: string | null;
  old_stamp: string | null;
  source_log_id: number | null;
  issued_by: string | null;
  status: RefundStatus;
  claimed_by: string | null;
  delivered_at: number | null;
  /** The identity the RETURNED item carries — deliberately not the old one. */
  new_stamp: string | null;
}

export interface PulledListing {
  rowId: number;
  logged_at: number;
  listing_id: number | null;
  price: number | null;
  seller_uuid: string | null;
  seller_name: string | null;
  listed_at: number | null;
  expires_at: number | null;
  item: Record<string, unknown> | null;
}

export interface DupeIncident {
  id: number;
  logged_at: number;
  server: string;
  player: string | null;
  message: string;
  stamp: string | null;
  kind: string | null;
  /**
   * "conclusive" — the same item in two slots of one inventory, which cannot happen honestly.
   * "circumstantial" — the same item on two players, which a trade or a lagged sync also looks like.
   * The UI must not present the second as guilt.
   */
  proof: string | null;
  voided_count: number | null;
  player_uuid: string | null;
  /** The item payload, shaped by the plugin. Loose because every field in it is optional. */
  item: Record<string, unknown> | null;
  /**
   * What the system did on its own. `banned` is ALWAYS false — there is no automatic server ban.
   * The anti-dupe system is trusted to take the item and stop the sale, both reversible, but not to
   * end an account. Staff ban by hand after reviewing, so the UI says "awaiting staff review".
   */
  enforcement: {
    banned: boolean;
    auction_banned: boolean;
    auction_ban_reason: string | null;
    auction_listings_pulled: number | null;
    logged_at: number;
  } | null;
  /** The `dupe-void` row's id — what a refund is issued against. Null means nothing to refund. */
  voidLogId: number | null;
  refund: RefundRecord | null;
  refundEvents: Array<{
    rowId: number;
    logged_at: number;
    refund_id: number | null;
    new_stamp: string | null;
    issued_by: string | null;
    dropped_on_ground: boolean;
    server: string;
  }>;
  /**
   * Auction listings destroyed as part of this incident, newest first.
   *
   * `seller_name` is not necessarily the flagged player: a duped item can be passed on and listed by
   * somebody else, which is a lead worth surfacing rather than a detail to flatten away.
   */
  listings: PulledListing[];
  /** True for rows written before the `details` column existed. */
  detailsMissing: boolean;
  /** Set when the payload was present but not valid JSON — shown raw rather than dropped. */
  raw: string | null;
}
